// src/components/proposal-formats/FormatCalcLineRow.tsx

import { useEffect, useState } from "react";
import type { FormatCalcLine } from "@/lib/opportunity/formatCalcLineTypes";
import type { UpsertFormatCalcLinePayload } from "@/lib/opportunity/formatCalcLineClient";

export type FormatCalcLineRowProps = {
  line: FormatCalcLine;
  evaluatedAmount: number | null;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onSave: (payload: UpsertFormatCalcLinePayload) => Promise<{ ok: boolean; error?: string }>;
  onDelete: (id: string) => Promise<{ ok: boolean; error?: string }>;
  onMove: (direction: "up" | "down") => void;
};

export function FormatCalcLineRow(props: FormatCalcLineRowProps) {
  const { line, evaluatedAmount, canMoveUp, canMoveDown, onSave, onDelete, onMove } = props;

  const [label, setLabel] = useState(line.label);
  const [amountMode, setAmountMode] = useState(line.amount_mode);
  const [defaultAmount, setDefaultAmount] = useState(String(line.default_amount));
  const [visible, setVisible] = useState(line.visible_on_proposal);
  const [commissionable, setCommissionable] = useState(line.commissionable);
  const [includeInDeposit, setIncludeInDeposit] = useState(line.include_in_deposit);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    setLabel(line.label);
    setAmountMode(line.amount_mode);
    setDefaultAmount(String(line.default_amount));
    setVisible(line.visible_on_proposal);
    setCommissionable(line.commissionable);
    setIncludeInDeposit(line.include_in_deposit);
    setMsg(null);
  }, [line.id, line.updated_at]);

  const dirty =
    label !== line.label ||
    amountMode !== line.amount_mode ||
    defaultAmount !== String(line.default_amount) ||
    visible !== line.visible_on_proposal ||
    commissionable !== line.commissionable ||
    includeInDeposit !== line.include_in_deposit;

  async function handleSave() {
    const amt = Number(defaultAmount);
    if (!label.trim() || !Number.isFinite(amt) || amt < 0) return;
    if (amountMode === "percent" && amt > 100) return;
    setBusy(true);
    setMsg(null);
    const r = await onSave({
      id: line.id,
      format_key: line.format_key,
      product_type: line.product_type,
      placement: line.placement,
      sort_order: line.sort_order,
      label: label.trim(),
      amount_mode: amountMode,
      default_amount: amt,
      visible_on_proposal: visible,
      commissionable,
      include_in_deposit: includeInDeposit,
    });
    setBusy(false);
    setMsg({ ok: r.ok, text: r.ok ? "Saved." : (r.error || "Save failed.") });
  }

  async function handleDelete() {
    if (!window.confirm(`Remove line "${line.label}"?`)) return;
    setBusy(true);
    const r = await onDelete(line.id);
    setBusy(false);
    if (!r.ok) setMsg({ ok: false, text: r.error || "Delete failed." });
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex flex-wrap items-start gap-2">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Line label"
          className="flex-1 min-w-[120px] text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-700"
        />
        <select
          value={amountMode}
          onChange={(e) => setAmountMode(e.target.value as "fixed" | "percent")}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-700"
        >
          <option value="fixed">$</option>
          <option value="percent">%</option>
        </select>
        <input
          type="number"
          min={0}
          max={amountMode === "percent" ? 100 : undefined}
          step="0.01"
          value={defaultAmount}
          onChange={(e) => setDefaultAmount(e.target.value)}
          className="w-24 text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-700"
        />
        {evaluatedAmount != null ? (
          <span className="text-xs text-gray-500 dark:text-zinc-400 tabular-nums pt-2">
            → ${evaluatedAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        ) : null}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={visible}
            onChange={(e) => setVisible(e.target.checked)}
          />
          Visible on proposal
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={commissionable}
            onChange={(e) => setCommissionable(e.target.checked)}
          />
          Commissionable
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={includeInDeposit}
            onChange={(e) => setIncludeInDeposit(e.target.checked)}
          />
          Include in deposit
        </label>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex gap-1">
          <button
            type="button"
            disabled={!canMoveUp || busy}
            onClick={() => onMove("up")}
            className="px-2 py-0.5 rounded border border-gray-300 text-[10px] disabled:opacity-40"
          >
            ↑
          </button>
          <button
            type="button"
            disabled={!canMoveDown || busy}
            onClick={() => onMove("down")}
            className="px-2 py-0.5 rounded border border-gray-300 text-[10px] disabled:opacity-40"
          >
            ↓
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleDelete()}
            className="px-2 py-0.5 rounded border border-red-300 text-[10px] text-red-700"
          >
            Remove
          </button>
        </div>
        <div className="flex items-center gap-2">
          {msg ? (
            <span className={msg.ok ? "text-emerald-600" : "text-red-600"}>{msg.text}</span>
          ) : null}
          <button
            type="button"
            disabled={busy || !dirty}
            onClick={() => void handleSave()}
            className="px-2.5 py-1 rounded-lg bg-indigo-600 text-white text-xs disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
